/**
 * PrivacySection Component
 *
 * PIPEDA Compliance - User data management:
 * - View personal data (Right to Access)
 * - Export data (Right to Data Portability)
 * - Manage cookie preferences (Right to Withdraw Consent)
 * - View consent history
 */

'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Download, Shield, Cookie, Eye, AlertCircle } from 'lucide-react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { SecondaryButton } from '@/src/shared/ui/button/SecondaryButton';
import { Toggle } from '@/src/shared/ui/Toggle';
import { toast } from 'sonner';
import {
  exportUserData,
  downloadUserDataAsJson,
  updateCookiePreferences,
  type ExportUserDataResponse,
  type CookiePreferences,
} from '../../api';

export function PrivacySection() {
  const t = useTranslations('settings.privacy');
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<ExportUserDataResponse | null>(null);
  const [showData, setShowData] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState<CookiePreferences>({
    essential: true,
    functional: true,
    analytics: false,
    marketing: false,
  });
  const [savingCookies, setSavingCookies] = useState(false);

  // Load user data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const data = await exportUserData();
      setUserData(data);
      // Load cookie preferences from user data
      if (data.userData.cookiePreferences) {
        setCookiePrefs(data.userData.cookiePreferences);
      }
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error(t('errors.loadFailed'));
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      const data = await exportUserData();
      downloadUserDataAsJson(data);
      toast.success(t('export.success'));
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(t('export.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCookiePreferences = async () => {
    setSavingCookies(true);
    try {
      await updateCookiePreferences(cookiePrefs);
      toast.success(t('cookies.saveSuccess'));
      // Reload user data to get updated preferences
      await loadUserData();
    } catch (error) {
      console.error('Failed to save cookie preferences:', error);
      toast.error(t('cookies.saveError'));
    } finally {
      setSavingCookies(false);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return t('common.never');
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Page Header */}
      <div>
        <Typography variant="h2" className="mb-2">
          {t('title')}
        </Typography>
        <Typography variant="bodySm" color="secondary">
          {t('description')}
        </Typography>
      </div>

      {/* PIPEDA Notice */}
      <div className="bg-accent-primary/10 border border-accent-primary/20 rounded-lg p-4 flex gap-3">
        <Shield className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
        <div>
          <Typography variant="bodyMd" weight="semibold" className="mb-1">
            {t('pipedaNotice.title')}
          </Typography>
          <Typography variant="bodySm" color="secondary">
            {t('pipedaNotice.description')}
          </Typography>
        </div>
      </div>

      {/* View My Data Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Eye className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('viewData.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('viewData.description')}
        </Typography>

        <SecondaryButton
          onClick={() => setShowData(!showData)}
          className="mb-4"
        >
          {showData ? t('viewData.hide') : t('viewData.show')}
        </SecondaryButton>

        {showData && userData && (
          <div className="bg-background-secondary rounded-lg p-4 space-y-3 border border-border-primary">
            <DataField label={t('viewData.fields.email')} value={userData.userData.email} />
            <DataField
              label={t('viewData.fields.verified')}
              value={userData.userData.isVerified ? t('common.yes') : t('common.no')}
            />
            <DataField
              label={t('viewData.fields.createdAt')}
              value={formatDate(userData.userData.createdAt)}
            />
            <DataField
              label={t('viewData.fields.lastLoginAt')}
              value={formatDate(userData.userData.lastLoginAt)}
            />
            <DataField
              label={t('viewData.fields.systemRoles')}
              value={userData.userData.systemRoles.join(', ') || t('common.none')}
            />
          </div>
        )}
      </section>

      {/* Export Data Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('export.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('export.description')}
        </Typography>

        <div className="bg-background-secondary border border-border-primary rounded-lg p-4">
          <Typography variant="bodyMd" weight="semibold" className="mb-2">
            {t('export.format')}
          </Typography>
          <Typography variant="bodySm" color="secondary" className="mb-4">
            {t('export.formatDescription')}
          </Typography>

          <PrimaryButton
            onClick={handleExportData}
            isLoading={loading}
            className="flex items-center gap-2"
          >
            <Download size={18} />
            {t('export.button')}
          </PrimaryButton>
        </div>
      </section>

      {/* Cookie Preferences Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Cookie className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('cookies.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('cookies.description')}
        </Typography>

        <div className="space-y-3">
          {/* Essential Cookies */}
          <div className="bg-background-secondary border border-border-primary rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Typography variant="bodyMd" weight="semibold">
                  {t('cookies.essential.title')}
                </Typography>
                <Typography variant="bodySm" color="secondary">
                  {t('cookies.essential.description')}
                </Typography>
              </div>
              <Toggle checked={true} disabled />
            </div>
          </div>

          {/* Functional Cookies */}
          <div className="border border-border-primary rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Typography variant="bodyMd" weight="semibold">
                  {t('cookies.functional.title')}
                </Typography>
                <Typography variant="bodySm" color="secondary">
                  {t('cookies.functional.description')}
                </Typography>
              </div>
              <Toggle
                checked={cookiePrefs.functional}
                onChange={(val) => setCookiePrefs({ ...cookiePrefs, functional: val })}
              />
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="border border-border-primary rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Typography variant="bodyMd" weight="semibold">
                  {t('cookies.analytics.title')}
                </Typography>
                <Typography variant="bodySm" color="secondary">
                  {t('cookies.analytics.description')}
                </Typography>
              </div>
              <Toggle
                checked={cookiePrefs.analytics}
                onChange={(val) => setCookiePrefs({ ...cookiePrefs, analytics: val })}
              />
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="border border-border-primary rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Typography variant="bodyMd" weight="semibold">
                  {t('cookies.marketing.title')}
                </Typography>
                <Typography variant="bodySm" color="secondary">
                  {t('cookies.marketing.description')}
                </Typography>
              </div>
              <Toggle
                checked={cookiePrefs.marketing}
                onChange={(val) => setCookiePrefs({ ...cookiePrefs, marketing: val })}
              />
            </div>
          </div>
        </div>

        <PrimaryButton
          onClick={handleSaveCookiePreferences}
          isLoading={savingCookies}
          className="mt-4"
        >
          {t('cookies.save')}
        </PrimaryButton>
      </section>

      {/* Consent History Section */}
      {userData && (
        <section className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-text-secondary" />
            <Typography variant="h3">{t('consent.title')}</Typography>
          </div>

          <Typography variant="bodySm" color="secondary" className="mb-4">
            {t('consent.description')}
          </Typography>

          <div className="border border-border-primary rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-background-secondary">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <Typography variant="bodySm" weight="semibold">
                      {t('consent.table.type')}
                    </Typography>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <Typography variant="bodySm" weight="semibold">
                      {t('consent.table.version')}
                    </Typography>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <Typography variant="bodySm" weight="semibold">
                      {t('consent.table.acceptedAt')}
                    </Typography>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                <tr>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{t('consent.types.terms')}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{userData.userData.termsVersion || '-'}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{formatDate(userData.userData.termsAcceptedAt)}</Typography>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{t('consent.types.privacy')}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{userData.userData.privacyVersion || '-'}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{formatDate(userData.userData.privacyAcceptedAt)}</Typography>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{t('consent.types.ageVerification')}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">
                      {userData.userData.ageVerified ? t('common.yes') : t('common.no')}
                    </Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{formatDate(userData.userData.ageVerifiedAt)}</Typography>
                  </td>
                </tr>
                <tr>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{t('consent.types.marketing')}</Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">
                      {userData.userData.marketingConsent ? t('common.yes') : t('common.no')}
                    </Typography>
                  </td>
                  <td className="px-4 py-3">
                    <Typography variant="bodySm">{formatDate(userData.userData.marketingConsentAt)}</Typography>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// Helper component for displaying data fields
function DataField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
      <Typography variant="bodySm" weight="semibold" className="sm:w-40">
        {label}:
      </Typography>
      <Typography variant="bodySm" color="secondary" className="flex-1">
        {value}
      </Typography>
    </div>
  );
}
